import { useEffect, useMemo, useRef, useState, type FocusEvent } from "react";
import {
  Button,
  ButtonGroup,
  ListBoxItem,
  Modal,
  Select,
  TextInput,
} from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import type { WorkPatternRow } from "../../../generated/core-api";
import {
  MAX_WORK_PATTERN_CODE_LENGTH,
  validateDayHoursFromStrings,
} from "../../hooks/WorkPatternImport/useWorkPatternImportCsvParser";
import {
  calendarInfoForCycleDay,
  cycleDayInputLabel,
  isValidCalendarDateIso,
} from "./workPatternCycleCalendar";
import styles from "./WorkPatternImportEditModal.module.css";

interface WorkPatternImportEditModalProps {
  isOpen: boolean;
  pattern: WorkPatternRow | null;
  patternIndex: number | null;
  onClose: () => void;
  onSave: (
    index: number,
    updated: WorkPatternRow
  ) => void | Promise<void>;
  /** Prevent modal actions while validation or commit is already in flight. */
  isProcessing?: boolean;
  /**
   * Temporary gate for the new edit-modal fields — work pattern name, cycle
   * days, pay public holidays (#435). Default/undefined = today's behaviour
   * (only `standardHours`, `standardDays`, `startDate`, per-day `dayHours`).
   * Remove once GA — see `FeatureFlag.EnableWorkPatternErrorEditInUi`.
   */
  enableErrorEditInUi?: boolean;
}

/** Convert ISO YYYY-MM-DD to DD/MM/YYYY for the edit field. */
function isoToDisplay(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Normalise DD/MM/YYYY, DD/MM/YY, or ISO to YYYY-MM-DD for the API payload. */
function displayToIso(value: string): string | null {
  const trimmed = value.trim();
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return trimmed;
  const dmyFull = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmyFull) {
    const dd = dmyFull[1].padStart(2, "0");
    const mm = dmyFull[2].padStart(2, "0");
    return `${dmyFull[3]}-${mm}-${dd}`;
  }
  const dmyShort = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (dmyShort) {
    const dd = dmyShort[1].padStart(2, "0");
    const mm = dmyShort[2].padStart(2, "0");
    return `20${dmyShort[3]}-${mm}-${dd}`;
  }
  return null;
}

function dayHoursToFormValues(dayHours: number[]): string[] {
  return dayHours.map((hours) => String(hours));
}

const MAX_CYCLE_DAYS = 365;

function resizeDayHourValues(
  prev: string[],
  parsed: number
): { values: string[]; resizeNotice: string | null } {
  if (parsed === prev.length) {
    return { values: prev, resizeNotice: null };
  }
  if (parsed > prev.length) {
    return {
      values: [...prev, ...Array(parsed - prev.length).fill("0")],
      resizeNotice: null,
    };
  }
  const removed = prev.length - parsed;
  return {
    values: prev.slice(0, parsed),
    resizeNotice: `Cycle days reduced to ${parsed} — removed ${removed} day-hour value${
      removed === 1 ? "" : "s"
    } from the end of the cycle.`,
  };
}

/** Normalise edited start date to ISO for calendar labels while typing. */
function resolveStartDateIso(startDateDisplay: string, fallbackIso: string): string {
  return displayToIso(startDateDisplay) ?? fallbackIso;
}

/**
 * R14 — Edit in UI: adjust declared standard values, per-day hours, and start
 * date before re-validation.
 */
export function WorkPatternImportEditModal({
  isOpen,
  pattern,
  patternIndex,
  onClose,
  onSave,
  isProcessing = false,
  enableErrorEditInUi = false,
}: WorkPatternImportEditModalProps) {
  const [workPatternName, setWorkPatternName] = useState("");
  const [workPatternCodeText, setWorkPatternCodeText] = useState("");
  const [standardHours, setStandardHours] = useState("");
  const [standardDays, setStandardDays] = useState("");
  const [startDate, setStartDate] = useState("");
  const [cycleDaysText, setCycleDaysText] = useState("");
  const [payPublicHolidays, setPayPublicHolidays] = useState<string | null>(
    null
  );
  const [dayHourValues, setDayHourValues] = useState<string[]>([]);
  const [dayHoursResizeNotice, setDayHoursResizeNotice] = useState<string | null>(
    null
  );
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cycleDaysTextRef = useRef("");
  const dayHourValuesRef = useRef<string[]>([]);
  const isSubmittingRef = useRef(false);

  const startDateIso = useMemo(
    () => (pattern ? resolveStartDateIso(startDate, pattern.startDate) : ""),
    [pattern, startDate]
  );

  const dayHourRoundingWarnings = useMemo(() => {
    if (!pattern || dayHourValues.length === 0) {
      return [];
    }
    const cycleDaysForWarnings = enableErrorEditInUi
      ? Number(cycleDaysText.trim())
      : pattern.cycleDays;
    const result = validateDayHoursFromStrings(dayHourValues, cycleDaysForWarnings);
    return result.roundingWarnings ?? [];
  }, [pattern, dayHourValues, enableErrorEditInUi, cycleDaysText]);

  useEffect(() => {
    if (!pattern) return;
    setWorkPatternName(pattern.workPatternName);
    setWorkPatternCodeText(pattern.workPatternCode ?? "");
    setStandardHours(String(pattern.standardHours));
    setStandardDays(String(pattern.standardDays));
    setStartDate(isoToDisplay(pattern.startDate));
    const cycleDaysString = String(pattern.cycleDays);
    setCycleDaysText(cycleDaysString);
    cycleDaysTextRef.current = cycleDaysString;
    setPayPublicHolidays(
      pattern.payPublicHolidays === "Yes" || pattern.payPublicHolidays === "No"
        ? pattern.payPublicHolidays
        : null
    );
    const dayHoursFormValues = dayHoursToFormValues(pattern.dayHours);
    setDayHourValues(dayHoursFormValues);
    dayHourValuesRef.current = dayHoursFormValues;
    setDayHoursResizeNotice(null);
    setFieldError(null);
  }, [pattern, patternIndex]);

  useEffect(() => {
    if (!isOpen) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [isOpen, patternIndex]);

  const handleDayHourChange = (index: number, value: string) => {
    setDayHourValues((prev) => {
      const next = [...prev];
      next[index] = value;
      dayHourValuesRef.current = next;
      return next;
    });
  };

  /** Update cycle-days text only — grid resize runs on blur / save (#435). */
  const handleCycleDaysChange = (value: string) => {
    cycleDaysTextRef.current = value;
    setCycleDaysText(value);
  };

  const applyCycleDaysGridResize = (value: string) => {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) {
      setDayHoursResizeNotice(null);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > MAX_CYCLE_DAYS) {
      setDayHoursResizeNotice(null);
      return;
    }
    const result = resizeDayHourValues(dayHourValuesRef.current, parsed);
    if (result.values === dayHourValuesRef.current) {
      return;
    }
    dayHourValuesRef.current = result.values;
    setDayHourValues(result.values);
    setDayHoursResizeNotice(result.resizeNotice);
    if (result.resizeNotice) {
      setFieldError(null);
    }
  };

  const handleCycleDaysBlur = (event?: FocusEvent<HTMLInputElement>) => {
    const value =
      event?.target?.value ?? cycleDaysTextRef.current ?? cycleDaysText;
    applyCycleDaysGridResize(value);
  };

  const handleSave = async () => {
    if (
      isProcessing ||
      isSubmittingRef.current ||
      pattern === null ||
      patternIndex === null
    ) {
      return;
    }

    if (enableErrorEditInUi && !workPatternName.trim()) {
      setFieldError("Work pattern name is required.");
      return;
    }

    const trimmedCode = workPatternCodeText.trim();
    if (
      enableErrorEditInUi &&
      trimmedCode.length > MAX_WORK_PATTERN_CODE_LENGTH
    ) {
      setFieldError(
        `Work pattern code must be ${MAX_WORK_PATTERN_CODE_LENGTH} characters or fewer.`
      );
      return;
    }

    if (
      enableErrorEditInUi &&
      payPublicHolidays !== "Yes" &&
      payPublicHolidays !== "No"
    ) {
      setFieldError("Select whether public holidays are paid.");
      return;
    }

    const hoursText = standardHours.trim();
    const daysText = standardDays.trim();
    if (!hoursText || !daysText) {
      setFieldError("Standard hours and days are required.");
      return;
    }

    const hours = Number(hoursText);
    const days = Number(daysText);
    if (!Number.isFinite(hours) || !Number.isFinite(days)) {
      setFieldError("Standard hours and days must be valid numbers.");
      return;
    }

    const isoStart = displayToIso(startDate);
    if (!isoStart) {
      setFieldError("Start date must be DD/MM/YYYY, DD/MM/YY, or YYYY-MM-DD.");
      return;
    }
    if (!isValidCalendarDateIso(isoStart)) {
      setFieldError(
        `Start date '${startDate.trim()}' is not a valid calendar date.`
      );
      return;
    }

    let cycleDaysValue = pattern.cycleDays;
    if (enableErrorEditInUi) {
      const trimmedCycleDays = cycleDaysText.trim();
      if (!/^\d+$/.test(trimmedCycleDays)) {
        setFieldError("Cycle days must be a whole number between 1 and 365.");
        return;
      }
      cycleDaysValue = Number(trimmedCycleDays);
      if (cycleDaysValue < 1 || cycleDaysValue > MAX_CYCLE_DAYS) {
        setFieldError("Cycle days must be between 1 and 365.");
        return;
      }
      const resized = resizeDayHourValues(dayHourValuesRef.current, cycleDaysValue);
      if (resized.values !== dayHourValuesRef.current) {
        dayHourValuesRef.current = resized.values;
        setDayHourValues(resized.values);
        if (resized.resizeNotice) {
          setDayHoursResizeNotice(resized.resizeNotice);
          setFieldError(null);
          return;
        }
      }
    }

    const dayHoursForSave = enableErrorEditInUi
      ? resizeDayHourValues(dayHourValuesRef.current, cycleDaysValue).values
      : dayHourValuesRef.current;

    const dayHoursResult = validateDayHoursFromStrings(
      dayHoursForSave,
      cycleDaysValue
    );
    if (dayHoursResult.error) {
      setFieldError(dayHoursResult.error);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onSave(patternIndex, {
        ...pattern,
        ...(enableErrorEditInUi
          ? {
              workPatternName: workPatternName.trim(),
              workPatternCode: trimmedCode === "" ? null : trimmedCode,
              cycleDays: cycleDaysValue,
              payPublicHolidays: payPublicHolidays as string,
            }
          : {}),
        standardHours: hours,
        standardDays: days,
        startDate: isoStart,
        dayHours: dayHoursResult.value!,
      });
      onClose();
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const actionsDisabled = isProcessing || isSubmitting;

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <Heading slot="title">Edit work pattern</Heading>
        </div>

        {pattern && !enableErrorEditInUi && (
          <p className={styles.patternMeta}>
            {pattern.workPatternName} · Employee Group {pattern.workPatternCode ?? ""}
          </p>
        )}

        <div className={styles.fieldGrid}>
          {enableErrorEditInUi && (
            <div className={styles.fullWidth}>
              <TextInput
                label="Work pattern name"
                value={workPatternName}
                onChange={setWorkPatternName}
                isRequired
              />
            </div>
          )}
          {enableErrorEditInUi && (
            <div className={styles.fullWidth}>
              <TextInput
                label="Work pattern code"
                value={workPatternCodeText}
                onChange={setWorkPatternCodeText}
              />
            </div>
          )}
          <TextInput
            label="Standard hours"
            value={standardHours}
            onChange={setStandardHours}
            isRequired
          />
          <TextInput
            label="Standard days"
            value={standardDays}
            onChange={setStandardDays}
            isRequired
          />
          {enableErrorEditInUi && (
            <TextInput
              label="Cycle days"
              value={cycleDaysText}
              onChange={handleCycleDaysChange}
              onBlur={handleCycleDaysBlur}
              isRequired
            />
          )}
          {enableErrorEditInUi && (
            <Select
              label="Pay public holidays"
              placeholder="Select an option"
              selectedKey={payPublicHolidays}
              onSelectionChange={(key) =>
                setPayPublicHolidays(key === null ? null : String(key))
              }
              isRequired
            >
              <ListBoxItem id="Yes">Yes</ListBoxItem>
              <ListBoxItem id="No">No</ListBoxItem>
            </Select>
          )}
          <div className={styles.fullWidth}>
            <TextInput
              label="Start date"
              value={startDate}
              onChange={setStartDate}
              isRequired
            />
          </div>

          {pattern && dayHourValues.length > 0 && (
            <div className={styles.dayHoursSection}>
              <p className={styles.dayHoursHeading}>Day hours</p>
              <div className={styles.dayHoursGrid}>
                {dayHourValues.map((value, index) => {
                  const { dow, date } = calendarInfoForCycleDay(startDateIso, index);
                  return (
                    <div key={index} className={styles.dayHourCell}>
                      <label className={styles.dayHourLabel}>
                        <span className={styles.cycleDay}>Day {index + 1}</span>
                        {dow && <span className={styles.dayMeta}>{dow}</span>}
                        {date && <span className={styles.dayDate}>{date}</span>}
                        <input
                          className={styles.dayHourInput}
                          type="text"
                          inputMode="decimal"
                          aria-label={cycleDayInputLabel(startDateIso, index)}
                          value={value}
                          onChange={(event) =>
                            handleDayHourChange(index, event.target.value)
                          }
                          required
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
              {dayHourRoundingWarnings.length > 0 && (
                <div role="status" className={styles.fieldWarning}>
                  {dayHourRoundingWarnings.map((warning) => (
                    <p key={warning} className={styles.fieldWarningLine}>
                      {warning}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        <div className={styles.modalFooter}>
          {(fieldError ||
            (enableErrorEditInUi && dayHoursResizeNotice !== null)) && (
            <div className={styles.formFeedback}>
              {fieldError && (
                <p role="alert" className={styles.fieldError}>
                  {fieldError}
                </p>
              )}
              {enableErrorEditInUi && dayHoursResizeNotice && (
                <p
                  role="status"
                  className={styles.resizeNoticeBanner}
                  data-testid="cycle-days-resize-notice"
                >
                  {dayHoursResizeNotice}
                </p>
              )}
            </div>
          )}

          <div className={styles.actions}>
            <ButtonGroup>
              <Button
                variant="tertiary"
                onPress={onClose}
                isDisabled={actionsDisabled}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={handleSave}
                isDisabled={actionsDisabled}
              >
                Save &amp; re-validate
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
    </Modal>
  );
}
